import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Loader2, Building2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { ResolvedAddress } from '@/admin/hooks/use-lookups'
import { useCreateHoaCommunity } from '@/admin/hooks/use-hoa-communities'
import { toastApiError } from '@/shared/lib/api-error'

// Stable reference so useForm's internal memos don't see a "new" deps
// array every render (which would rebuild the form and drop input focus).
const EMPTY_FORM_DEPS: unknown[] = []

type FormValues = {
  hoa_name: string
  manager_name: string
  manager_email: string
  manager_phone: string
  total_units: string | number
}

/**
 * Dedicated "Register HOA Community" screen (Step A).
 *
 * Captures the community in one place — name, property-manager contact,
 * community address, total units — and creates the community via
 * POST /registrations/hoa-community/. Units (with products/warranties) are
 * added afterwards on the community; the community itself has no product.
 */
export function RegisterHoaCommunityPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // When launched from a developer node, nest this community under it.
  const developerUuid = searchParams.get('developer')
  const [address, setAddress] = useState<ResolvedAddress | null>(null)
  const createCommunity = useCreateHoaCommunity()

  // Update a single field of the composite community address.
  const setAddrField = (key: keyof ResolvedAddress, val: string) =>
    setAddress((prev) => ({
      line_1: '',
      line_2: '',
      town_or_city: '',
      county: '',
      postcode: '',
      country: '',
      ...(prev ?? {}),
      [key]: val,
    }))

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'hoa_name',
        label: 'HOA / Community Name',
        placeholder: 'e.g. Maple Grove HOA',
        value: '',
        required: 'Community name is required',
      },
      {
        type: 'number',
        name: 'total_units',
        label: 'Total Units in Community',
        placeholder: 'e.g. 48',
        value: '',
        hint: 'How many units / houses / apartments are in this community in total?',
      },
      {
        type: 'text',
        name: 'manager_name',
        label: 'Property Manager - Full Name',
        placeholder: 'e.g. Sara Khan',
        value: '',
      },
      {
        type: 'email',
        name: 'manager_email',
        label: 'Property Manager — Email',
        placeholder: 'manager@community.com',
        value: '',
        required: 'Property manager email is required',
        validationSchema: z
          .string()
          .trim()
          .email('Enter a valid email')
          .min(1, 'Property manager email is required'),
      },
      {
        type: 'phone',
        name: 'manager_phone',
        label: 'Property Manager — Phone',
        value: '',
      },
    ],
    [],
  )

  const { Form } = useForm(
    {
      name: 'register-hoa-community-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as FormValues
      if (!address || !address.postcode || !address.line_1) {
        toast.error('Community address is required')
        return
      }
      const totalUnitsRaw = values.total_units
      const totalUnits =
        totalUnitsRaw === '' || totalUnitsRaw === null || totalUnitsRaw === undefined
          ? null
          : Number(totalUnitsRaw)
      // Split the manager's full name into first/last for the backend
      // (which stores them separately on the consumer record).
      const nameParts = String(values.manager_name ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
      const managerFirst = nameParts[0] ?? ''
      const managerLast = nameParts.slice(1).join(' ')
      try {
        const community = await createCommunity.mutateAsync({
          hoa_name: String(values.hoa_name).trim(),
          total_units: totalUnits,
          manager_email: String(values.manager_email).trim(),
          manager_first_name: managerFirst,
          manager_last_name: managerLast,
          manager_phone: String(values.manager_phone ?? '').trim(),
          developer_partnership_uuid: developerUuid,
          address: {
            addressLine1: address.line_1,
            addressLine2: address.line_2,
            locality: address.town_or_city,
            region: address.county,
            postalCode: address.postcode,
            countryCode: address.country,
          },
        })
        toast.success(`HOA community "${community.hoa_name}" registered`)
        navigate('/hoa-communities')
      } catch (err) {
        toastApiError(err, 'Failed to register HOA community')
      }
    },
    // Stable deps: useForm keeps the latest submit handler in a live ref,
    // so the handler always sees the current `address` without listing it
    // here. Passing a changing array would rebuild the form and drop the
    // cursor mid-typing.
    EMPTY_FORM_DEPS,
  )

  return (
    <AppShell>
      <main className="page-container py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-[var(--bg-subtle)]">
              <Building2 size={18} className="text-[var(--ink-3)]" />
            </div>
            <div>
              <h1 className="font-display text-[28px] leading-none text-[var(--ink)]">
                Register HOA Community
              </h1>
              <p className="mt-1.5 text-[12px] text-[var(--ink-3)]">
                Set up the community once — its units and claims hang off it.
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/hoa-communities')}
            className="cursor-pointer text-[11px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            Cancel
          </Button>
        </div>

        <div className="grid max-w-[920px] grid-cols-1 gap-4 md:grid-cols-2">
          {/* Community + manager */}
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
            <p className="mb-4 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
              Community &amp; Manager
            </p>
            <Form className="flex flex-col gap-4" />
          </div>

          {/* Address - full inline form */}
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
            <p className="mb-4 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
              Community Address
            </p>
            <div className="flex flex-col gap-3">
              <div>
                <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Address Line 1
                </Label>
                <Input
                  value={address?.line_1 ?? ''}
                  onChange={(e) => setAddrField('line_1', e.target.value)}
                  placeholder="Street address"
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Address Line 2
                </Label>
                <Input
                  value={address?.line_2 ?? ''}
                  onChange={(e) => setAddrField('line_2', e.target.value)}
                  placeholder="Apartment, suite, building (optional)"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    City / Town
                  </Label>
                  <Input
                    value={address?.town_or_city ?? ''}
                    onChange={(e) => setAddrField('town_or_city', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    State / County
                  </Label>
                  <Input
                    value={address?.county ?? ''}
                    onChange={(e) => setAddrField('county', e.target.value)}
                    placeholder="State / county"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    ZIP / Postcode
                  </Label>
                  <Input
                    value={address?.postcode ?? ''}
                    onChange={(e) => setAddrField('postcode', e.target.value)}
                    placeholder="ZIP / postcode"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    Country
                  </Label>
                  <Input
                    value={address?.country ?? ''}
                    onChange={(e) => setAddrField('country', e.target.value)}
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] leading-[1.5] text-[var(--ink-3)]">
              This is the community&apos;s base address. Individual unit numbers
              are captured per unit when you add them.
            </p>
          </div>
        </div>

        {/* Footer actions */}
        <div className="mt-6 flex max-w-[920px] items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/hoa-communities')}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="register-hoa-community-form"
            disabled={createCommunity.isPending}
            className="cursor-pointer gap-1.5"
          >
            {createCommunity.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            Register Community
          </Button>
        </div>
      </main>
    </AppShell>
  )
}
